import { z } from 'zod';
import { PINS_BLOQUEADOS, TIPOS_PRODUCTO } from './constants';
import { limpiarTextoNota } from './notasRapidas';

/**
 * Validadores Zod compartidos entre main y renderer.
 * Single source of truth para reglas de negocio.
 */

// =============================================================================
// NEGOCIO
// =============================================================================

const RFC_REGEX = /^([A-ZÑ&]{3,4})\d{6}([A-Z\d]{3})?$/;

/**
 * Días de la semana en que algo aplica.
 *
 * Sprint 74 — Vivía en la sección de promociones, que es donde nació (Sprint 7.5). Sube aquí porque
 * ahora también lo usan los paquetes, y `PaqueteSchema` se declara antes: un `const` no se puede
 * usar arriba de su declaración.
 */
export const DiasSemanaSchema = z.object({
  lunes: z.boolean(),
  martes: z.boolean(),
  miercoles: z.boolean(),
  jueves: z.boolean(),
  viernes: z.boolean(),
  sabado: z.boolean(),
  domingo: z.boolean(),
});
export type DiasSemanaDTO = z.infer<typeof DiasSemanaSchema>;

export const NegocioSchema = z.object({
  nombre: z.string().min(2, 'El nombre debe tener al menos 2 caracteres').max(100),
  rfc: z
    .string()
    .optional()
    .refine(
      (v) => !v || v.length === 0 || RFC_REGEX.test(v.toUpperCase()),
      'Ese RFC no parece válido. Déjalo vacío si no lo recuerdas.',
    ),
  direccion: z.string().max(200).optional(),
  telefono: z.string().max(20).optional(),
  email: z.string().email('Email no válido').optional().or(z.literal('')),
  redesSociales: z.string().optional(),
  colorPrimario: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#1976D2'),
  monedaCodigo: z.string().length(3).default('MXN'),
  monedaSimbolo: z.string().min(1).max(3).default('$'),
  locale: z.string().default('es-MX'),
  mensajePieTicket: z.string().max(200).optional(),
  /**
   * Sprint 130 — Cómo pedir factura, en palabras del negocio.
   *
   * Texto libre a propósito: los plazos y las condiciones de facturación los fija el contador del
   * negocio, no este sistema. La cadena vacía es válida y significa "no facturo": el ticket no
   * imprime nada.
   */
  leyendaFiscal: z.string().max(240).optional(),
  /**
   * Sprint 111 — Dónde está el negocio, para medir la distancia al cliente.
   *
   * Rangos reales del planeta, y `nullable` porque **vaciarlas es una acción legítima**: si el
   * negocio se muda y todavía no ubica el nuevo local, es preferible que el sistema deje de
   * sugerir a que siga sugiriendo desde la dirección vieja.
   */
  latitud: z.number().min(-90).max(90).nullable().optional(),
  longitud: z.number().min(-180).max(180).nullable().optional(),
  /**
   * Sprint 92 — El menú público y su compra mínima.
   *
   * Los dos son **decisión del negocio**, no del código: encender una puerta a internet y decidir
   * por cuánto vale la pena mandar la moto. El mínimo va en centavos y `0` significa sin mínimo,
   * que es lo que la mayoría de los negocios quiere el primer día.
   */
  menuPublicoActivo: z.boolean().optional(),
  compraMinima: z.number().int().min(0).max(100_000_00).optional(),
  /** Sprint 96 — Tope de pedidos sin confirmar por teléfono. 0 = sin tope. */
  maxPedidosSinConfirmar: z.number().int().min(0).max(20).optional(),
  /** Sprint 96 — Ajustar el total a los centavos del folio para reconocer el depósito. */
  centavosDeFolio: z.boolean().optional(),
  /** Sprint 137 — Ofrecer "pago al recibir" en el enlace público. Apagado de fábrica. */
  contraEntregaEnEnlace: z.boolean().optional(),
  /**
   * Sprint 98 — La plantilla de pedido por WhatsApp.
   *
   * Tope de 2000 caracteres: es un mensaje de chat, no un documento, y un campo de texto libre que
   * viaja del renderer al servidor necesita un límite aunque hoy sólo lo escriba la dueña.
   * Cadena vacía = volver a la canónica.
   */
  plantillaPedidoWa: z.string().max(2000).optional().nullable(),
  /**
   * Sprint 106 — Días de conservación del comprobante. `0` = para siempre.
   *
   * El tope de 3650 (diez años) no es burocracia: un número absurdo aquí se traduce en una base
   * que crece sin que nadie lo note hasta que el respaldo tarda media hora.
   */
  diasConservarComprobante: z.number().int().min(0).max(3650).optional(),
});
export type NegocioDTO = z.infer<typeof NegocioSchema>;

// =============================================================================
// USUARIOS
// =============================================================================

export const PinSchema = z
  .string()
  .regex(/^\d{4}$/, 'El PIN debe tener exactamente 4 dígitos')
  .refine((v) => !PINS_BLOQUEADOS.includes(v), 'Elige un PIN menos predecible');

export const CrearDuenoSchema = z
  .object({
    nombre: z.string().min(2).max(50),
    pin: PinSchema,
    pinConfirmacion: PinSchema,
    fotoPath: z.string().optional(),
    emailSoporte: z.string().email().optional().or(z.literal('')),
    consentimientoTelemetria: z.boolean().default(false),
  })
  .refine((d) => d.pin === d.pinConfirmacion, {
    message: 'Los PIN no coinciden. Inténtalo de nuevo.',
    path: ['pinConfirmacion'],
  });
export type CrearDuenoDTO = z.infer<typeof CrearDuenoSchema>;

export const CrearMeseroSchema = z.object({
  nombre: z.string().min(2).max(50),
  fotoPath: z.string().optional(),
});
export type CrearMeseroDTO = z.infer<typeof CrearMeseroSchema>;

/**
 * Sprint 64 — Alta de usuario para los CUATRO oficios.
 *
 * Reemplaza al par `CrearDuenoSchema` / `CrearMeseroSchema` en el canal `usuarios:crear`.
 * Existía un hueco que la auditoría encontró: el handler validaba con `CrearMeseroSchema`
 * —que no tiene `rol` ni `pin`— y forzaba `rol: 'mesero'`. Pedir "cocina" creaba un mesero,
 * o sea que el error concedía MÁS permisos de los pedidos, y el PIN se descartaba.
 *
 * **El PIN es obligatorio salvo que la persona no vaya a entrar al sistema.** Ese caso
 * existe y es del negocio: cocineros y lavaplatos que sólo entran al reparto de propinas
 * (`activoLogin: false`, Sprint 12.1b). Pedirles un PIN que nadie va a teclear sería ruido.
 */
export const CrearUsuarioSchema = z
  .object({
    nombre: z.string().min(2).max(50),
    rol: z.enum(['dueño', 'caja', 'cocina', 'mesero']),
    pin: PinSchema.optional(),
    fotoPath: z.string().optional(),
    activoLogin: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    const entraAlSistema = v.activoLogin !== false;
    if (entraAlSistema && !v.pin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pin'],
        message: 'Asigna un PIN de 4 dígitos para que pueda entrar',
      });
    }
    // Un dueño sin acceso no tiene sentido: es quien responde por el negocio.
    if (v.rol === 'dueño' && v.activoLogin === false) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['activoLogin'],
        message: 'El dueño siempre necesita poder entrar',
      });
    }
  });
export type CrearUsuarioDTO = z.infer<typeof CrearUsuarioSchema>;

/** Sprint 66 — Alta o baja de publicidad de un cliente. */
export const ConsentimientoClienteSchema = z.object({
  clienteId: z.number().int().positive(),
  acepta: z.boolean(),
});
export type ConsentimientoClienteDTO = z.infer<typeof ConsentimientoClienteSchema>;

export const ActualizarUsuarioSchema = z.object({
  id: z.number().int().positive(),
  nombre: z.string().min(2).max(50).optional(),
  fotoPath: z.string().optional(),
  activo: z.boolean().optional(),
  /** Sprint 12.1d — bug fix: el handler ignoraba este campo aunque la UI lo enviaba. */
  activoLogin: z.boolean().optional(),
  /**
   * Sprint 94 — Cómo se presenta esta persona. **No es el rol y no toca ni un permiso.**
   * Vacío = se muestra el nombre del rol.
   */
  puesto: z.string().trim().max(40).nullable().optional(),
  pin: PinSchema.optional(),
});
export type ActualizarUsuarioDTO = z.infer<typeof ActualizarUsuarioSchema>;

/**
 * Sprint 9.2 — Cambio de PIN con re-auth.
 *
 * Reglas:
 *   - `pinActual` y `pinNuevo` deben ser 4 dígitos (PinSchema).
 *   - El nuevo NO puede ser igual al actual (defensa contra "cambiar para
 *     auditoría sin cambiar nada").
 *   - PINS_BLOQUEADOS aplica indirectamente: `pinNuevo` pasa por PinSchema
 *     que ya rechaza la blacklist (1234, 0000, etc.).
 *
 * Anti-IDOR: el handler verifica además que `usuarioId` coincida con la
 * sesión activa antes de invocar este schema.
 */
export const CambiarPinSchema = z
  .object({
    usuarioId: z.number().int().positive(),
    pinActual: z.string().regex(/^\d{4}$/, 'El PIN actual debe ser de 4 dígitos'),
    pinNuevo: PinSchema,
  })
  .refine((d) => d.pinNuevo !== d.pinActual, {
    message: 'El nuevo PIN debe ser distinto al actual',
    path: ['pinNuevo'],
  });
export type CambiarPinDTO = z.infer<typeof CambiarPinSchema>;

/**
 * Sprint 9.4 — 2FA TOTP.
 *
 * Validadores para el flujo de enrollment + activación + desactivación
 * + verificación del segundo factor. PIN re-auth obligatorio en todas las
 * operaciones administrativas del 2FA.
 */
export const TotpReAuthSchema = z.object({
  usuarioId: z.number().int().positive(),
  pinActual: z.string().regex(/^\d{4}$/, 'PIN inválido'),
});
export type TotpReAuthDTO = z.infer<typeof TotpReAuthSchema>;

export const TotpActivarSchema = z.object({
  usuarioId: z.number().int().positive(),
  pinActual: z.string().regex(/^\d{4}$/, 'PIN inválido'),
  codigo: z.string().regex(/^\d{6}$/, 'El código TOTP debe ser de 6 dígitos'),
});
export type TotpActivarDTO = z.infer<typeof TotpActivarSchema>;

/**
 * El usuario puede pegar el código de 6 dígitos (TOTP) o un backup code de
 * 8 chars alfanuméricos. La validación específica vive en el handler.
 */
export const Verificar2FASchema = z.object({
  codigo: z
    .string()
    .trim()
    .min(6, 'Código demasiado corto')
    .max(20, 'Código demasiado largo'),
});
export type Verificar2FADTO = z.infer<typeof Verificar2FASchema>;

// Sprint 32 — Step-up usa el mismo formato (TOTP de 6 dígitos o backup de 8 chars).
export const VerificarStepUpSchema = Verificar2FASchema;
export type VerificarStepUpDTO = z.infer<typeof VerificarStepUpSchema>;

// =============================================================================
// REPORTES POR RANGO (Sprint 36)
// =============================================================================

/**
 * Sprint 36 — Validación de rango de fechas para reportes prediseñados.
 * Acepta fechas ISO. `desde` <= `hasta`, ambos requeridos.
 * Tope de 1 año para evitar consultas absurdamente grandes.
 */
export const RangoFechasSchema = z.object({
  desde: z.string().datetime({ offset: true }).or(z.string().regex(/^\d{4}-\d{2}-\d{2}/, 'Fecha inválida')),
  hasta: z.string().datetime({ offset: true }).or(z.string().regex(/^\d{4}-\d{2}-\d{2}/, 'Fecha inválida')),
}).refine(
  (v) => new Date(v.desde) <= new Date(v.hasta),
  { message: 'La fecha "desde" debe ser anterior o igual a "hasta"' },
).refine(
  (v) => (new Date(v.hasta).getTime() - new Date(v.desde).getTime()) <= 366 * 24 * 60 * 60 * 1000,
  { message: 'El rango no puede ser mayor a 366 días' },
);
export type RangoFechasDTO = z.infer<typeof RangoFechasSchema>;

/**
 * Sprint 152 (DT-152) — **Cuántas filas puede pedir una lista.**
 *
 * Nace por el reporte de mejores clientes: su `limite` viajaba sin validar desde la pantalla hasta
 * un `slice`, así que pedir 999999 devolvía la agenda entera —nombre, teléfono y gasto de todos
 * los clientes a domicilio—. Son datos personales de clientes finales del negocio.
 *
 * Cincuenta es más de lo que nadie lee en pantalla y bastante menos que una base de datos. Si
 * alguna vez hace falta la lista completa, será por una puerta que lo diga en su nombre y que el
 * dueño pueda ver en la tabla de permisos, no por un número en un parámetro.
 */
export const LimiteListaSchema = z.coerce
  .number()
  .int()
  .min(1)
  .max(50)
  .catch(5)
  .default(5);

// =============================================================================
// CLIENTES (Sprint 35)
// =============================================================================

/**
 * Sprint 35 — Lookup de cliente recurrente. El teléfono se normaliza en el
 * service; aquí solo validamos longitud mínima razonable y caracteres
 * permitidos (dígitos, espacios, guiones, paréntesis, +).
 */
export const ClientePorTelefonoSchema = z.object({
  telefono: z
    .string()
    .trim()
    .min(4, 'Teléfono demasiado corto')
    .max(30, 'Teléfono demasiado largo')
    .regex(/^[\d\s()+-]+$/, 'Caracteres no válidos en el teléfono'),
});
export type ClientePorTelefonoDTO = z.infer<typeof ClientePorTelefonoSchema>;

// =============================================================================
// BACKUP & CORTE (Sprint 33 — cierra gaps de Zod del análisis forense)
// =============================================================================

/**
 * Sprint 33 — Whitelist estricta de nombres de backup. Bloquea path traversal
 * y solo permite los formatos que genera `BackupService.crearConTag()`.
 * Patrones soportados:
 *   - `backup-2026-05-23T17-42-54-arranque.zip`
 *   - `backup-2026-05-23T17-42-54-cierre-dia.zip`
 *   - `corrupt-backup-...-zip` (marcado como corrupto, no debería usarse para restaurar)
 */
export const BackupFilenameSchema = z
  .object({
    filename: z
      .string()
      .min(1, 'Nombre vacío')
      .max(200, 'Nombre demasiado largo')
      .regex(
        /^(corrupt-)?backup-\d{4}-\d{2}-\d{2}T\d{2}-\d{2}-\d{2}-[a-z-]+\.zip$/,
        'Formato de nombre de backup inválido',
      ),
  });
export type BackupFilenameDTO = z.infer<typeof BackupFilenameSchema>;

/**
 * Sprint 33 — Schema discriminado para CORTE_CERRAR. Mantiene compat con el
 * formato legacy (`string` suelto = solo observaciones) y agrega validación
 * estricta cuando viene como objeto con conteo físico.
 */
export const CorteCerrarPayloadSchema = z.union([
  z.string().max(500).optional(),
  z.object({
    montoFinalDeclarado: z.number().nonnegative('El monto no puede ser negativo').optional(),
    observaciones: z.string().max(500).optional(),
  }),
]).optional();
export type CorteCerrarPayloadDTO = z.infer<typeof CorteCerrarPayloadSchema>;

/** Sprint 33 — Apertura de corte con `montoInicial` validado. */
export const CorteAbrirSchema = z.object({
  montoInicial: z.number().nonnegative('El monto inicial no puede ser negativo').max(1_000_000),
  usuarioId: z.number().int().positive(),
});
export type CorteAbrirDTO = z.infer<typeof CorteAbrirSchema>;

// =============================================================================
// LOGO (Sprint 33)
// =============================================================================

/**
 * Sprint 33 — Valida que el base64 del logo tenga prefijo data:image/. Esto
 * NO valida el contenido binario (sharp lo hará al procesar), pero previene
 * que se pase texto arbitrario al servicio. Tope de 5 MB en base64 (~3.7 MB
 * binario).
 */
const MAX_BASE64_BYTES = 7_000_000; // ~5 MB binario en base64
export const LogoBase64Schema = z.object({
  base64: z
    .string()
    .min(20, 'Imagen vacía o inválida')
    .max(MAX_BASE64_BYTES, 'La imagen es demasiado grande')
    .refine(
      (s) => /^data:image\/(png|jpeg|jpg|webp|gif);base64,/i.test(s),
      'Formato de imagen no soportado (usa PNG, JPEG, WEBP o GIF)',
    ),
});
export type LogoBase64DTO = z.infer<typeof LogoBase64Schema>;

// =============================================================================
// PROMOS — Simular (Sprint 33)
// =============================================================================

/**
 * Sprint 33 — Validación mínima del input al motor de promociones.
 * No reusa los schemas de promoción CRUD (esos validan estructura completa).
 * Aquí solo necesitamos que sea un array de objetos con campos esperados.
 */
export const PromosSimularItemSchema = z.object({
  productoId: z.number().int().positive().optional(),
  paqueteId: z.number().int().positive().optional(),
  cantidad: z.number().positive(),
  precioUnitario: z.number().nonnegative(),
}).passthrough(); // permite campos extra que el motor pueda usar
export const PromosSimularSchema = z.array(PromosSimularItemSchema).max(500);
export type PromosSimularDTO = z.infer<typeof PromosSimularSchema>;

// =============================================================================
// CATÁLOGO
// =============================================================================

export const CategoriaSchema = z.object({
  nombre: z.string().min(1).max(50),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  icono: z.string().optional(),
  posicion: z.number().int().optional(),
  habilitado: z.boolean().default(true),
});
export type CategoriaDTO = z.infer<typeof CategoriaSchema>;

const UnidadVentaEnum = z.enum(['kg', 'pieza', 'litro', 'porcion']);

/**
 * Base ZodObject — permite `.partial()` para updates parciales (PATCH-like).
 * Usar `ProductoSchema` (con refine) para creaciones completas.
 */
/**
 * Sprint 63 — Porciones de un ingrediente.
 *
 * La revisión fina (nombre vs cantidad, repetidos, múltiplos de la fracción) vive en
 * `shared/porciones.ts` y la aplica el servicio: aquí sólo se acota la forma del DTO
 * para que nada absurdo llegue más adentro.
 */
export const PorcionSchema = z.object({
  etiqueta: z.string().min(1, 'Falta el nombre de la porción').max(40),
  cantidad: z.number().positive('La cantidad debe ser mayor a 0'),
  precio: z.number().int().positive('El precio debe ser mayor a 0'),
});

/**
 * Sprint 72 — Crear varios productos desde el inventario de un golpe.
 *
 * El precio va en centavos enteros como todo el dinero del sistema, y el consumo con hasta tres
 * decimales porque una fracción legítima es 0.5 litro o 0.25 kg.
 */
export const CrearDesdeInventarioSchema = z.object({
  categoriaId: z.number().int().positive().optional(),
    /** Sprint 165 — La persona confirmó que es otro producto aunque el nombre se parezca a uno que ya existe. */
    nombreParecidoConfirmado: z.boolean().optional(),
  items: z
    .array(
      z.object({
        insumoId: z.number().int().positive(),
        nombre: z.string().min(2, 'El nombre necesita al menos 2 letras').max(80),
        precio: z.number().int('El precio va en centavos enteros').positive('Ponle precio'),
        consumo: z.number().positive('El consumo por venta tiene que ser mayor a 0'),
      }),
    )
    .min(1, 'Selecciona al menos un ingrediente')
    // Son 27 en el catálogo real del cliente; 60 deja margen sin volverlo una transacción eterna.
    .max(60, 'Demasiados de un golpe: hazlo en dos tandas'),
});
export type CrearDesdeInventarioDTO = z.infer<typeof CrearDesdeInventarioSchema>;

/**
 * Sprint 82 — Alta de lo que se vende, con sus formatos, en una sola captura.
 *
 * Es la propuesta del dueño: *"da de alta el producto y al mismo tiempo sus formatos de venta, que
 * básicamente es su MENÚ"*. Por debajo sigue creando un ingrediente y N productos; lo que
 * desaparece es la segunda captura.
 */
export const AltaConFormatosSchema = z
  .object({
    nombre: z.string().trim().min(2, '¿Cómo se llama lo que vas a vender?').max(80),
    unidad: z.string().trim().min(1).max(20),
    categoriaId: z.number().int().positive().optional(),
    llevaInventario: z.boolean(),
    /** En la unidad base: 10 (kg). */
    cantidadInicial: z.number().min(0).max(1_000_000).optional(),
    /** "Cargo 10 y me quedan 6" = 40. */
    mermaCoccionPct: z.number().min(0).max(100).optional(),
    /*
      Sprint 127 — Aquí venía `fraccionMinima` desde la pantalla. Ya no: el servidor la deduce de
      los formatos. Un dato que el cliente puede mandar es un dato en el que hay que confiar, y de
      ese default (`0.25` para todo lo que se pesa) salió el rechazo de los 125 g de costilla.
    */
    stockMinimo: z.number().min(0).max(1_000_000).optional(),
    formatos: z.array(PorcionSchema).min(1, 'Agrega al menos un formato de venta').max(12),
    /** Sprint 165 — La persona confirmó que es otro producto aunque el nombre se parezca a uno que ya existe. */
    nombreParecidoConfirmado: z.boolean().optional(),
  })
  .refine((d) => !d.llevaInventario || (d.cantidadInicial ?? 0) >= 0, {
    message: '¿Cuánto tienes hoy?',
    path: ['cantidadInicial'],
  });
export type AltaConFormatosDTO = z.infer<typeof AltaConFormatosSchema>;

/**
 * Sprint 84 — Editar el grupo tal como se dio de alta.
 *
 * `productoId` presente = ese tamaño ya existe y se actualiza. Ausente = es nuevo. Los que no
 * vienen en la lista se quitan: se ocultan si ya se vendieron, se borran si nunca.
 */
/**
 * Sprint 91 — Enseñarle al sistema un apodo del cliente.
 *
 * El texto viene de un mensaje de WhatsApp, o sea de un tercero: se acota aquí. El límite de 60
 * caracteres no es estético — un "apodo" de párrafo entero no volvería a coincidir nunca y sólo
 * engordaría la tabla que se consulta en cada pedido.
 */
export const AprenderApodoSchema = z.object({
  texto: z.string().trim().min(2).max(60),
  productoId: z.number().int().positive(),
});

export const OlvidarApodoSchema = z.object({
  texto: z.string().trim().min(1).max(60),
});

export const ActualizarGrupoSchema = z.object({
  insumoId: z.number().int().positive(),
  categoriaId: z.number().int().positive().optional(),
  /**
   * Sprint 87 — Corregir el nombre y la unidad.
   *
   * Mismos límites que el alta: lo que se pudo teclear al crear tiene que poderse teclear al
   * corregir. Un validador más estricto de este lado convierte un typo en una condena.
   */
  nombre: z.string().trim().min(2, '¿Cómo se llama lo que vendes?').max(60).optional(),
  unidad: z.string().trim().min(1).max(20).optional(),
  mermaCoccionPct: z.number().min(0).max(100).optional(),
  /*
    Sprint 127 — Igual que en el alta: la divisibilidad ya no se recibe, se deduce de `formatos`.
  */
  formatos: z
    .array(
      z.object({
        productoId: z.number().int().positive().optional(),
        etiqueta: z.string().trim().min(1, 'Ponle nombre al tamaño').max(40),
        cantidad: z.number().positive('¿Cuánto lleva este tamaño?').max(10_000),
        precio: z.number().int().min(0).max(100_000_000),
      }),
    )
    .min(1, 'Deja al menos un tamaño de venta')
    .max(12),
});
export type ActualizarGrupoDTO = z.infer<typeof ActualizarGrupoSchema>;

/**
 * Sprint 110 — Retirar del menú. Uno de los dos, nunca ninguno.
 *
 * Sin el `refine`, un objeto vacío llegaría al servicio y `findMany({ where: { id: undefined } })`
 * devolvería **el catálogo entero**: un descuido de una línea que borraría el menú completo. Es
 * exactamente el tipo de operación en la que el validador tiene que ser paranoico.
 */
export const RetirarGrupoSchema = z
  .object({
    insumoId: z.number().int().positive().optional(),
    productoId: z.number().int().positive().optional(),
  })
  .refine((d) => d.insumoId !== undefined || d.productoId !== undefined, {
    message: '¿Qué se retira del menú?',
  });

/** Sprint 82 — Agregar un tamaño a algo que ya se vende, sin duplicar el ingrediente. */
export const AgregarFormatoSchema = z.object({
  insumoId: z.number().int().positive(),
  categoriaId: z.number().int().positive().optional(),
  formato: PorcionSchema,
});
export type AgregarFormatoDTO = z.infer<typeof AgregarFormatoSchema>;

export const CrearPorcionesSchema = z.object({
  insumoId: z.number().int().positive(),
  categoriaId: z.number().int().positive().optional(),
  porciones: z.array(PorcionSchema).min(1, 'Agrega al menos una porción').max(12),
});

/**
 * Sprint 63 (Fase 4) — Unidad de medida del catálogo.
 *
 * `codigo` es lo que cada insumo guarda, así que el servicio lo normaliza (minúsculas,
 * sin acentos ni espacios) antes de escribirlo: una unidad "Bolsa " y otra "bolsa" serían
 * dos inventarios distintos de lo mismo.
 */
export const CrearUnidadMedidaSchema = z.object({
  codigo: z.string().min(2, 'El código necesita al menos 2 letras').max(20),
  nombre: z.string().min(2, 'Falta el nombre de la unidad').max(40),
  descripcion: z.string().max(120).optional(),
  decimales: z.number().int().min(0).max(3).optional(),
  fraccionSugerida: z.number().positive().max(1).nullable().optional(),
  orden: z.number().int().min(0).max(9999).optional(),
});

export const ActualizarUnidadMedidaSchema = z.object({
  nombre: z.string().min(2).max(40).optional(),
  descripcion: z.string().max(120).nullable().optional(),
  decimales: z.number().int().min(0).max(3).optional(),
  fraccionSugerida: z.number().positive().max(1).nullable().optional(),
  orden: z.number().int().min(0).max(9999).optional(),
  activo: z.boolean().optional(),
});

export const ProductoBaseSchema = z.object({
  nombre: z.string().min(1).max(100),
    /** Sprint 165 — La persona confirmó que es otro producto aunque el nombre se parezca a uno que ya existe. */
    nombreParecidoConfirmado: z.boolean().optional(),
  /**
   * Sprint 62 — `tipo` ya no se captura: era obligatorio en pantalla y **ningún
   * lugar de la aplicación lo leía** (ni POS, ni cocina, ni reportes, ni tickets).
   * Eran etiquetas del sprint 0 para este cliente —corte / pollo / complemento /
   * paquete— que además competían con Categoría, que sí es editable, y traían un
   * valor "paquete" que no tiene relación con los paquetes reales.
   *
   * Se mantiene con default para no romper la columna ni los registros existentes;
   * retirarlo del esquema es deuda registrada (DT-046).
   */
  tipo: z.enum(TIPOS_PRODUCTO).default('complemento'),
  categoriaId: z.number().int().positive().optional(),
  descripcion: z.string().max(500).optional(),
  precio: z.number().positive('El precio debe ser mayor a 0'),
  /** Sprint 7.4. Precio del producto cuando se vende dentro de un paquete (fallback `precio` si null). */
  precioEnPaquete: z.number().positive('El precio en paquete debe ser mayor a 0').nullable().optional(),
  /**
   * Sprint 62 — Stock propio del producto: modelo retirado. El inventario vive en el
   * insumo; un producto sin insumo no lleva cuenta. Se aceptan por compatibilidad de
   * la columna, pero ninguna pantalla los envía ya.
   */
  inventarioActual: z.number().int().min(0, 'El inventario no puede ser negativo').default(0),
  inventarioMinimo: z.number().int().min(0).default(0),
  habilitado: z.boolean().default(true),
  imagenUrl: z.string().optional(),
  costoBase: z.number().min(0).optional(),
  /** Sprint 7.1. Unidad de venta para mostrar en POS. */
  unidadVenta: UnidadVentaEnum.default('pieza'),
  /** Sprint 7.1. Vínculo opcional al insumo del que se descuenta este producto. */
  insumoId: z.number().int().positive().nullable().optional(),
  /** Sprint 7.1. Cantidad consumida del insumo por unidad vendida. Requerido si insumoId presente. */
  gramaje: z.number().positive().nullable().optional(),
  /**
   * Sprint 63 (Fase 2b) — Receta: los ingredientes que lleva UNA unidad del producto.
   *
   * Con un solo renglón equivale al vínculo `insumoId`/`gramaje` de siempre. Con varios
   * expresa un platillo compuesto (pan + carne + queso), que es lo que el modelo viejo
   * no podía representar y lo que impedía vender el sistema a otro giro.
   */
  receta: z
    .array(
      z.object({
        insumoId: z.number().int().positive(),
        cantidad: z.number().positive('La cantidad de cada ingrediente debe ser mayor a 0'),
      }),
    )
    .max(20)
    .optional(),
});

export const ProductoSchema = ProductoBaseSchema.refine(
  (d) => {
    if (!d.receta || d.receta.length === 0) return true;
    const ids = d.receta.map((r) => r.insumoId);
    return new Set(ids).size === ids.length;
  },
  { message: 'Un ingrediente no puede estar dos veces en la misma receta.', path: ['receta'] },
).refine(
  (d) => !d.insumoId || (d.gramaje !== null && d.gramaje !== undefined && d.gramaje > 0),
  {
    message: 'Si vinculas un insumo, debes especificar el gramaje (cantidad consumida).',
    path: ['gramaje'],
  },
);
export type ProductoDTO = z.infer<typeof ProductoSchema>;

// =============================================================================
// ONBOARDING
// =============================================================================

export const OnboardingPaso1Schema = NegocioSchema.pick({
  nombre: true,
  rfc: true,
  direccion: true,
  telefono: true,
  email: true,
});
export type OnboardingPaso1DTO = z.infer<typeof OnboardingPaso1Schema>;

export const OnboardingPaso2Schema = z.object({
  colorPrimario: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#1976D2'),
  // logoFileBase64 procesado por el main
  logoFileBase64: z.string().optional(),
});
export type OnboardingPaso2DTO = z.infer<typeof OnboardingPaso2Schema>;

export const OnboardingPaso3Schema = z.object({
  cobraImpuestos: z.boolean().default(true),
  impuestoNombre: z.string().max(20).default('IVA'),
  impuestoPorcentaje: z.number().min(0).max(100).default(16),
  monedaCodigo: z.string().default('MXN'),
});
export type OnboardingPaso3DTO = z.infer<typeof OnboardingPaso3Schema>;

export const OnboardingPaso4Schema = CrearDuenoSchema;
export type OnboardingPaso4DTO = CrearDuenoDTO;

export const OnboardingPaso5Schema = z.object({
  cargarDemo: z.boolean().default(true),
});
export type OnboardingPaso5DTO = z.infer<typeof OnboardingPaso5Schema>;

// =============================================================================
// AUTH
// =============================================================================

export const LoginDuenoSchema = z.object({
  usuarioId: z.number().int().positive(),
  pin: z.string().regex(/^\d{4}$/),
});
export type LoginDuenoDTO = z.infer<typeof LoginDuenoSchema>;

export const LoginMeseroSchema = z.object({
  usuarioId: z.number().int().positive(),
});
export type LoginMeseroDTO = z.infer<typeof LoginMeseroSchema>;

// =============================================================================
// PAQUETES — Sprint 7.4: modelo unificado (Producto del catálogo + sustitutos)
// =============================================================================

/**
 * Sprint 7.4 (refactor Sprint 14). Renglón de un paquete: producto del catálogo
 * con CUÁNTAS unidades (entero) y opcionalmente lista de sustitutos válidos.
 *
 * RN-S7.4-02: cada renglón = (productoId, cantidad, posición).
 * RN-S7.4-03/04: sustitutos son IDs de Producto del catálogo.
 *
 * Sprint 14: se eliminó el campo `unidad` del DTO — era redundante con
 * `Producto.unidadVenta` y no se usaba en cálculos. El backend deriva la
 * unidad del producto base al persistir y exige misma unidadVenta en los
 * sustitutos (validarSustitutosCoherentes).
 *
 * Sprint 14: `cantidad` es ENTERO ≥ 1 ("cuántas unidades del producto van
 * en el paquete"). La porción/gramaje vive en Producto.gramaje.
 */
export const PaqueteProductoEditarSchema = z.object({
  productoId: z.number().int().positive('Debe elegir un producto del catálogo'),
  cantidad: z.number().int().min(1, 'Cuántos debe ser al menos 1'),
  posicion: z.number().int().min(1),
  esObligatorio: z.boolean().default(true),
  /** Sprint 44. Si true, el cliente puede pedir excluir este renglón en POS. */
  excluible: z.boolean().default(false),
  /**
   * Sprint 73 — El renglón va de regalo. El servicio fuerza `excluible` cuando es `true`: si
   * el regalo se acaba y no hay sustituto, hay que poder quitarlo y bajar el precio.
   */
  esRegalo: z.boolean().default(false),
  /** Lista de Producto.id válidos como sustitutos para este renglón. */
  sustitutosIds: z.array(z.number().int().positive()).default([]),
});
export type PaqueteProductoEditarDTO = z.infer<typeof PaqueteProductoEditarSchema>;

export const PaqueteSchema = z
  .object({
    nombre: z.string().min(1).max(100),
    descripcion: z.string().max(500).optional(),
    precioBase: z.number().positive('El precio base debe ser mayor a 0'),
    categoriaId: z.number().int().positive().optional(),
    habilitado: z.boolean().default(true),
    imagenUrl: z.string().optional(),
    /*
      Sprint 74 — Ventana de disponibilidad del paquete.

      *Promo Amigos* ($599) y *Gratis medio pollo* ($299) son paquetes con precio de promoción y
      horario ("de miércoles a viernes de 1PM a 5PM"). Todo en null = disponible siempre, que es
      cómo se comportaban todos los paquetes hasta ahora.

      `nullable` además de `optional`: el formulario manda null explícito al quitar la restricción.
    */
    vigenciaInicio: z.string().min(1).nullable().optional(),
    vigenciaFin: z.string().min(1).nullable().optional(),
    diasSemana: DiasSemanaSchema.nullable().optional(),
    horaInicio: z.number().int().min(0).max(1439).nullable().optional(),
    horaFin: z.number().int().min(0).max(1439).nullable().optional(),
    productos: z
      .array(PaqueteProductoEditarSchema)
      .min(2, 'RN-S7.4-01: un paquete requiere mínimo 2 productos'),
  })
  .refine(
    // Sprint 74 — Las dos horas van juntas: con una sola, la ventana se ignora y el paquete estaría
    // disponible todo el día mientras la dueña cree haberle puesto horario.
    (d) => ((d.horaInicio ?? null) === null) === ((d.horaFin ?? null) === null),
    {
      message: 'Pon las dos horas o ninguna: con una sola el paquete estaría disponible todo el día',
      path: ['horaFin'],
    },
  )
  .refine(
    (d) =>
      !d.vigenciaInicio ||
      !d.vigenciaFin ||
      new Date(d.vigenciaFin).getTime() > new Date(d.vigenciaInicio).getTime(),
    { message: 'La fecha de fin debe ser posterior a la de inicio', path: ['vigenciaFin'] },
  )
  .refine(
    (d) => {
      const posiciones = d.productos.map((p) => p.posicion);
      return new Set(posiciones).size === posiciones.length;
    },
    { message: 'Las posiciones de los renglones no pueden repetirse', path: ['productos'] },
  )
  .refine(
    (d) =>
      d.productos.every(
        (p) => !p.sustitutosIds.includes(p.productoId),
      ),
    {
      message: 'Un producto no puede ser sustituto de sí mismo',
      path: ['productos'],
    },
  )
  .refine(
    (d) =>
      d.productos.every(
        (p) => new Set(p.sustitutosIds).size === p.sustitutosIds.length,
      ),
    {
      message: 'Hay sustitutos duplicados en algún renglón',
      path: ['productos'],
    },
  );
export type PaqueteDTO = z.infer<typeof PaqueteSchema>;

// =============================================================================
// PEDIDOS
// =============================================================================

/**
 * Sprint 7.4. Selección por renglón al vender un paquete: para cada PaqueteProducto
 * el POS envía qué producto final se sirvió (default o sustituto). El backend valida
 * contra el catálogo de sustitutos del renglón.
 */
export const PedidoItemPaqueteProductoInputSchema = z.object({
  paqueteProductoId: z.number().int().positive(),
  /**
   * Sprint 44. ID del producto final servido. Puede ser 0 / cualquier valor
   * cuando `excluido=true` (se ignora). Validador exige positivo sólo cuando
   * el renglón NO está excluido.
   */
  productoFinalId: z.number().int(),
  /** Sprint 44. Si true, el cliente excluyó este renglón del paquete. */
  excluido: z.boolean().optional(),
}).refine(
  (d) => d.excluido === true || d.productoFinalId > 0,
  { message: 'Falta el producto seleccionado para el renglón', path: ['productoFinalId'] },
);
export type PedidoItemPaqueteProductoInputDTO = z.infer<typeof PedidoItemPaqueteProductoInputSchema>;

export const PedidoItemSchema = z.object({
  productoId: z.number().int().positive().optional(),
  paqueteId: z.number().int().positive().optional(),
  cantidad: z.number().int().min(1, 'La cantidad debe ser al menos 1'),
  /**
   * Sprint 131 — Venta al peso: cuánto pesa o mide este renglón, en la unidad base.
   *
   * El tope de 1000 es una barrera de dedazo, no una regla del negocio: nadie vende mil kilos en
   * un renglón, y un `350` tecleado donde iban gramos (0.35) tiene que rebotar aquí y no acabar
   * descontando trescientos cincuenta kilos del almacén.
   *
   * **El precio NO viaja**: lo calcula el servidor con el precio por kilo del producto. Si el
   * cliente pudiera mandar el importe, vender al peso sería la puerta para cobrar lo que uno
   * quiera.
   */
  cantidadMedida: z.number().positive().max(1000).optional(),
  /**
   * Sprint 138 — Cómo lo quiere el cliente ("Término medio · Bien picada").
   *
   * Sprint 139 (hallazgo de la auditoría) — **se sanea aquí, no sólo en la pantalla.**
   *
   * El saneo nació en la puerta pública (el menú en línea) y esta —la del POS— se quedó como
   * estaba: el mismo patrón que ya pagamos en el Sprint 137, *"blindé un canal y no el hermano"*.
   * Y era explotable desde el rol más bajo que vende: `aTextoImprimible` limpia los bytes de
   * control de la impresora pero **conserva el salto de línea**, así que un `pedidos:crear`
   * fabricado a mano con `"medio\n\nTOTAL PAGADO $0.00"` escribía renglones reales en el ticket
   * del cliente y en el PDF. No es inyección de comandos: es **falsificar un documento de cobro**.
   *
   * `transform` y no `refine` a propósito: rechazar obligaría al operador legítimo a adivinar qué
   * carácter sobra. Se limpia y se sigue vendiendo — nunca se bloquea una venta por un dato de
   * captura (doctrina §9).
   */
  /*
    La lambda es obligatoria: `transform(limpiarTextoNota)` le pasaría el `ctx` de Zod como
    segundo argumento, que es el `limite` de la función — y `slice(0, ctx)` devuelve cadena
    vacía. Es decir: **borraría todas las notas en silencio**. Lo cazó su propio test.
  */
  notasEspeciales: z.string().max(500).transform((s) => limpiarTextoNota(s)).optional(),
  /** Sprint 7.4. Selección de productos finales por renglón cuando es paquete. */
  paqueteProductos: z.array(PedidoItemPaqueteProductoInputSchema).optional(),
  /** Sprint 7. Override manual de precio (sólo dueño desde POS, requiere motivo). */
  precioOverride: z.number().min(0).optional(),
  motivoOverride: z.string().min(3).max(200).optional(),
}).refine((d) => d.productoId || d.paqueteId, {
  message: 'Debe especificar productoId o paqueteId',
}).refine((d) => d.cantidadMedida === undefined || (d.productoId !== undefined && d.cantidad === 1), {
  // Un renglón al peso es UNA pesada de un producto: ni un paquete, ni "3 × 350 g".
  message: 'Una venta al peso es un solo renglón de un producto',
  path: ['cantidadMedida'],
}).refine((d) => !d.precioOverride || (d.motivoOverride && d.motivoOverride.length >= 3), {
  message: 'Override de precio requiere motivo (mínimo 3 caracteres)',
}).refine(
  (d) => !d.paqueteId || (d.paqueteProductos && d.paqueteProductos.length > 0),
  { message: 'Un ítem de paquete requiere selección de productos finales', path: ['paqueteProductos'] },
);
export type PedidoItemDTO = z.infer<typeof PedidoItemSchema>;

/**
 * Sprint 159 — Clave de un intento de captura: un UUID aleatorio, nunca el nulo. Una pantalla con
 * fallo que mandara siempre `0000…` haría que todo saliera "repetido" y dejara de crear pedidos.
 */
export const claveIntentoSchema = z
  .string()
  .uuid()
  .refine((v) => !/^0{8}-0{4}-0{4}-0{4}-0{12}$/.test(v), 'Clave de captura inválida')
  .optional();

/**
 * Sprint 160 — "¿Cuánto contaste?" de la tarjeta Cerrar ayer. Centavos enteros; `null` = "no lo
 * conté". El tope atrapa el dedazo de escribir centavos dos veces (un día de Las Espadas ronda los
 * $10,000; $1,000,000 en la gaveta es un error de captura, no una venta).
 */
export const DeclararConteoSchema = z.object({
  corteId: z.number().int().positive(),
  montoContado: z
    .number()
    .int('El monto se maneja en centavos')
    .min(0, 'No puede ser negativo')
    .max(100_000_000, 'Esa cantidad parece un error de dedo')
    .nullable(),
});

export const CreatePedidoSchema = z.object({
  tipo: z.enum(['comer_aqui', 'delivery']),
  /** Sprint 161 — 'enlace' no se acepta de la pantalla: sólo lo pone el menú público. */
  canalEntrada: z.enum(['chat', 'chat_tecleado', 'telefono', 'pos']).optional(),
  /**
   * Sprint 159 — Clave del intento de captura. Tiene que estar en el esquema: un campo que Zod no
   * conoce se borra en silencio y la protección contra el doble toque no llegaría al servicio.
   */
  claveIntento: claveIntentoSchema,
  /**
   * Sprint 104 — Venta de mostrador. Va siempre con `comer_aqui` y sin mesa; el servicio lo
   * comprueba. Se valida aquí porque todo lo que cruza el boundary se valida aquí.
   */
  paraRecoger: z.boolean().optional(),
  mesaId: z.number().int().positive().optional(),
  usuarioId: z.number().int().positive(),
  /// Sprint 12.1b — mesero que atiende físicamente (obligatorio, puede ser la dueña).
  meseroAtiendeId: z.number().int().positive({
    message: 'Debes seleccionar quién atiende esta mesa',
  }),
  /// Sprint 12.1d — Cantidad de comensales en la mesa (informativo + pre-default división de cuenta).
  cantidadComensales: z.number().int().positive().optional(),
  items: z.array(PedidoItemSchema).min(1, 'El pedido debe tener al menos un ítem'),
  /** Nota general del pedido. Se sanea por lo mismo que `notasEspeciales`: también va a papel. */
  notas: z.string().max(500).transform((s) => limpiarTextoNota(s)).optional(),

  // Cliente delivery
  clienteNombre: z.string().max(100).optional(),
  clienteTelefono: z.string().max(20).optional(),
  clienteDireccion: z.string().max(200).optional(),
  /** Sprint 7.1. Coordenadas del domicilio (Leaflet+OSM). */
  clienteLatitud: z.number().min(-90).max(90).optional(),
  clienteLongitud: z.number().min(-180).max(180).optional(),
  /** Sprint 7.1. Notas para el repartidor (referencia visual, color casa, timbre). */
  referenciasEntrega: z.string().max(500).optional(),
  /** Costo de envío para delivery. Default 0. */
  costoEnvio: z.number().min(0).default(0),
  /** true = repartidor cobra envío al cliente al recibir; false = incluido en total. */
  envioPagaCliente: z.boolean().default(false),
  /**
   * Sprint 76 — Quién viene por el pedido, según el grupo MOTO MANDADO.
   *
   * Se captura al cotizar porque ahí es cuando se sabe. En despacho ya sólo se confirma.
   */
  nombreRepartidor: z.string().trim().min(2).max(80).optional(),
  /**
   * Sprint 76 — El envío ya se cotizó (aunque el costo sea 0).
   *
   * Sin `.default(true)` a propósito: un pedido que se guarda **antes** de preguntarle al grupo
   * tiene que poder decir "todavía no", y ése es justamente el caso nuevo del sprint.
   */
  envioCotizado: z.boolean().default(true),
  /**
   * Sprint 137 — El pedido sale sin cobrar y se cobra al entregarlo.
   *
   * Se acepta al crear —y no sólo al completar el envío— porque el camino normal del domicilio
   * captura todo de un tirón: cliente, menú y envío. Quien lo autoriza sale de la sesión
   * (`firmadoPorLaSesion`), nunca del navegador.
   */
  pagaAlEntregar: z.boolean().optional(),
  /** Con cuánto va a pagar, en centavos. Sólo para que la moto lleve el cambio. */
  pagaCon: z
    .number()
    .int('El monto se maneja en centavos')
    .positive('Con cuánto paga tiene que ser mayor que cero')
    .max(10_000_000, 'Ese monto parece un error de dedo')
    .optional(),
  /**
   * Sprint 66 — Consentimiento de publicidad. **Sin `.default()` a propósito**: aquí
   * `undefined` significa "no se preguntó" y tiene que llegar así al servicio, porque un
   * `false` por default le apagaría la suscripción a quien ya la había aceptado cada vez
   * que vuelve a pedir.
   */
  aceptaPublicidad: z.boolean().optional(),
}).refine((d) => d.tipo !== 'delivery' || (d.clienteNombre && d.clienteDireccion), {
  message: 'Delivery requiere nombre y dirección del cliente',
  path: ['clienteDireccion'],
});
export type CreatePedidoDTO = z.infer<typeof CreatePedidoSchema>;

/**
 * Sprint 78 — Preguntar si el carrito se puede vender, antes de capturar nada más.
 *
 * Reusa `PedidoItemSchema` para que lo que se valida sea exactamente lo que después se manda.
 */
export const ValidarCarritoSchema = z.object({
  items: z.array(PedidoItemSchema).max(200),
});
export type ValidarCarritoDTO = z.infer<typeof ValidarCarritoSchema>;

/**
 * Sprint 28 — Agregar productos a un pedido de mesa (comer_aqui) en curso.
 * Reusa `PedidoItemSchema` para que paquetes, sustituciones y overrides se
 * validen igual que en la creación. El servicio valida estado/tipo del pedido.
 */
export const AgregarItemsSchema = z.object({
  pedidoId: z.number().int().positive(),
  usuarioId: z.number().int().positive(),
  items: z.array(PedidoItemSchema).min(1, 'Agrega al menos un producto'),
  /**
   * DT-080 — Versión del pedido que tenía la pantalla al abrirse.
   *
   * Opcional para que el servidor pueda desplegarse antes de que todas las pantallas la
   * manden: sin ella el comportamiento es el de siempre. Cuando viene, el servicio la
   * compara y rechaza si alguien escribió en medio.
   */
  version: z.number().int().nonnegative().optional(),
});
export type AgregarItemsDTO = z.infer<typeof AgregarItemsSchema>;

/**
 * Sprint 29 — Volver a juntar una cuenta dividida. `pedidoId` puede ser el
 * pedido padre o cualquiera de sus hijos; el servicio resuelve la familia.
 */
export const ReunificarCuentaSchema = z.object({
  pedidoId: z.number().int().positive(),
  usuarioId: z.number().int().positive(),
});
export type ReunificarCuentaDTO = z.infer<typeof ReunificarCuentaSchema>;

/** Sprint 172 — Entrega de turno. Los PIN se comprueban en el servidor; aquí sólo la forma. */
export const EntregarTurnoSchema = z.object({
  pedidoIds: z.array(z.number().int().positive()).min(1, 'Escoge al menos una mesa').max(60),
  aUsuarioId: z.number().int().positive({ message: 'Escoge a quién se las entregas' }),
  pinPropio: z.string().regex(/^[0-9]{4}$/, 'El PIN es de 4 números'),
  pinRecibe: z.string().regex(/^[0-9]{4}$/, 'El PIN es de 4 números').optional(),
}).strict();
/** Para COMPROBAR un PIN ya existente (no para elegir uno: ése es `PinSchema`, que rechaza los débiles). */
export const PinIngresadoSchema = z.string().regex(/^[0-9]{4}$/, 'El PIN es de 4 números');

/**
 * @deprecated v1.1 — usar `CobrarMixtoSchema`. Se conserva para tests legacy
 * y compatibilidad de tipos en otros consumidores.
 */
export const CobroSchema = z.object({
  pedidoId: z.number().int().positive(),
  metodoPago: z.enum(['efectivo', 'tarjeta', 'transferencia']),
  montoRecibido: z.number().min(0).optional(),
});
export type CobroDTO = z.infer<typeof CobroSchema>;

/** Un pago individual dentro de un cobro mixto. */
export const PagoSchema = z.object({
  metodo: z.enum(['efectivo', 'tarjeta', 'transferencia']),
  /**
   * Sprint 69 (H-11) — `.int()` en los dos importes.
   *
   * Todo el dinero del sistema son centavos enteros, pero el esquema aceptaba decimales: un
   * `propina: 9.285` pasaba la validación, la columna `Int` lo truncaba a 9, y quedaba un centavo
   * fantasma entre lo que se le cobró al cliente y lo que registró la base. La UI ya manda
   * enteros; esto cierra la puerta para el bridge web y para cualquier script.
   */
  monto: z.number().int('El monto va en centavos enteros').positive('El monto del pago debe ser mayor a 0'),
  propina: z
    .number()
    .int('La propina va en centavos enteros')
    .min(0, 'La propina no puede ser negativa')
    // Mismo tope que el fondo de caja: por encima de eso es un dedo gordo, no una propina.
    .max(1_000_000, 'Esa propina es demasiado alta. Revisa la cantidad.')
    .default(0),
  referencia: z.string().max(100).optional(),
});
export type PagoDTO = z.infer<typeof PagoSchema>;

/**
 * Cobro mixto: lista de pagos cuya suma debe igualar el total del pedido.
 * La validación de cuadre se hace en el service (que conoce el total real).
 */
export const CobrarMixtoSchema = z.object({
  pedidoId: z.number().int().positive(),
  pagos: z.array(PagoSchema).min(1, 'Debe registrarse al menos un pago'),
  /** Para calcular cambio del primer pago en efectivo (opcional). */
  montoRecibidoEfectivo: z.number().min(0).optional(),
});
export type CobrarMixtoDTO = z.infer<typeof CobrarMixtoSchema>;

/**
 * Sprint 139 — Corregir la forma de pago de un cobro ya registrado.
 *
 * El importe **no viaja como decisión del cliente**: el servicio comprueba que la suma coincida
 * con lo que ya se había cobrado y rechaza cualquier otra cosa. Aquí sólo se acota la forma.
 *
 * `usuarioId` lo pone el servidor con `firmadoPorLaSesion`, nunca el navegador: un acta de
 * corrección firmada por quien diga la pantalla no sirve para auditar nada.
 */
export const CorregirFormaPagoSchema = z.object({
  pedidoId: z.number().int().positive(),
  pagos: z
    .array(
      PagoSchema.extend({
        /** Con cuánto pagó, sólo en efectivo: es lo que deja al ticket decir el cambio. */
        recibido: z.number().int().positive().optional(),
      }),
    )
    .min(1, 'Debe registrarse al menos una forma de pago')
    .max(5, 'Demasiadas formas de pago para un mismo cobro'),
  /**
   * Por qué se corrige. Obligatorio y con mínimo real: es lo que distingue una corrección
   * legítima de un descuadre tapado, y lo único que va a quedar para explicarlo dentro de un mes.
   */
  motivo: z.string().trim().min(5, 'Escribe por qué se corrige').max(200),
  usuarioId: z.number().int().positive(),
  /**
   * Sprint 139 (H-1) — PIN del dueño. Lo exige el **servicio** cuando la corrección baja el
   * efectivo esperado del corte, comparando contra los pagos que hay en la base.
   *
   * Opcional aquí y obligatorio allá a propósito: quien decide si esta operación necesita
   * autorización no puede ser el navegador que la pide.
   */
  supervisorPin: z.string().regex(/^\d{4}$/).optional(),
});
export type CorregirFormaPagoDTO = z.infer<typeof CorregirFormaPagoSchema>;

/**
 * Sprint 141 — Cobrar la diferencia de un pedido al que se le agregó algo.
 *
 * El importe **no lo decide quien llama**: el servicio comprueba que sea exactamente lo que
 * falta, restando lo pagado del total. Aquí sólo se acota la forma.
 */
export const CobrarDiferenciaSchema = z.object({
  pedidoId: z.number().int().positive(),
  pagos: z
    .array(
      PagoSchema.extend({
        recibido: z.number().int().positive().optional(),
      }),
    )
    .max(5, 'Demasiadas formas de pago para un mismo cobro')
    .default([]),
  /** Se cobra en la puerta. Sólo domicilio, y el servicio comprueba que no haya salido. */
  alEntregar: z.boolean().optional(),
  usuarioId: z.number().int().positive(),
});
export type CobrarDiferenciaDTO = z.infer<typeof CobrarDiferenciaSchema>;

export const CancelarPedidoSchema = z.object({
  pedidoId: z.number().int().positive(),
  razon: z.string().min(1, 'La razón es obligatoria').max(500),
  usuarioId: z.number().int().positive(),
  /** Sprint 8 RN-S8-05: requerido cuando el pedido ya está `en_ruta`. */
  supervisorPin: z.string().regex(/^\d{4}$/).optional(),
});
export type CancelarPedidoDTO = z.infer<typeof CancelarPedidoSchema>;

/**
 * Cancelación de un ítem específico (no del pedido entero).
 * Requiere PIN del dueño (supervisor) — vector antifraude clásico.
 */
export const CancelarItemSchema = z.object({
  itemId: z.number().int().positive(),
  motivo: z.string().min(10, 'El motivo debe tener al menos 10 caracteres').max(500),
  usuarioId: z.number().int().positive(),
  supervisorPin: z.string().regex(/^\d{4}$/, 'PIN del supervisor inválido'),
});
export type CancelarItemDTO = z.infer<typeof CancelarItemSchema>;

/** Mover un pedido en curso a otra mesa. */
export const MoverMesaSchema = z.object({
  pedidoId: z.number().int().positive(),
  nuevaMesaId: z.number().int().positive(),
  usuarioId: z.number().int().positive(),
});
export type MoverMesaDTO = z.infer<typeof MoverMesaSchema>;

/** Sprint 6 RN-S6-01: agregar mesas a un pedido en curso. */
export const JuntarMesasSchema = z.object({
  pedidoId: z.number().int().positive(),
  mesaIds: z.array(z.number().int().positive()).min(1, 'Debe indicarse al menos una mesa'),
  usuarioId: z.number().int().positive(),
});
export type JuntarMesasDTO = z.infer<typeof JuntarMesasSchema>;

/** Sprint 6 RN-S6-02: quitar mesas adicionales de un pedido. */
export const SepararMesasSchema = z.object({
  pedidoId: z.number().int().positive(),
  mesaIds: z.array(z.number().int().positive()).min(1, 'Debe indicarse al menos una mesa'),
  usuarioId: z.number().int().positive(),
});
export type SepararMesasDTO = z.infer<typeof SepararMesasSchema>;

/** Un grupo de items dentro de una división de cuenta. */
export const GrupoCuentaSchema = z.object({
  items: z.array(z.number().int().positive()).min(1, 'Cada grupo debe tener al menos un item'),
  etiqueta: z.string().max(50).optional(),
});
export type GrupoCuentaDTO = z.infer<typeof GrupoCuentaSchema>;

/**
 * Sprint 7.2: adjuntar comprobante de transferencia para delivery.
 * `archivoBase64`: contenido del archivo en base64 (sin prefijo data:...).
 * `extension`: png|jpg|jpeg|webp|pdf — la magic-byte check vive en el service.
 * Cota de 8 MB en base64 ≈ 6 MB binario; el service rechaza definitivamente >5 MB.
 */
export const AdjuntarComprobanteSchema = z.object({
  pedidoId: z.number().int().positive(),
  archivoBase64: z
    .string()
    .min(1, 'El archivo está vacío')
    .max(8 * 1024 * 1024, 'El archivo excede el tamaño máximo permitido'),
  extension: z.enum(['png', 'jpg', 'jpeg', 'webp', 'pdf']),
  usuarioId: z.number().int().positive(),
  /**
   * Sprint 77 — Cuánto dice la captura, en centavos.
   *
   * Opcional: el negocio ya revisa el monto a ojo, así que teclearlo es ayuda, no obligación. Un
   * campo obligatorio más en el paso donde el cliente está esperando sería la forma segura de
   * que se teclee cualquier cosa con tal de avanzar.
   */
  montoDeclarado: z.number().int().nonnegative().max(100_000_000).optional(),
  /**
   * Continuar aunque el monto declarado no cuadre con el total.
   *
   * Se manda a propósito, con la diferencia ya a la vista, y queda registrado quién lo autorizó.
   */
  autorizarDiferencia: z.boolean().optional(),
});
export type AdjuntarComprobanteDTO = z.infer<typeof AdjuntarComprobanteSchema>;

/**
 * Sprint 79 — Confirmar el pago sin comprobante adjunto.
 *
 * `usuarioId` lo pone la sesión (`firmadoPorLaSesion`), nunca el cliente: la firma de quien
 * verificó el depósito es justamente lo que sustituye a la imagen.
 */
export const ConfirmarPagoVerificadoSchema = z.object({
  pedidoId: z.number().int().positive(),
  usuarioId: z.number().int().positive(),
  /** Lo que se vio en la cuenta, en centavos. Opcional: a veces sólo se ve que entró. */
  montoVerificado: z.number().int().nonnegative().max(100_000_000).optional(),
  nota: z.string().trim().max(200).optional(),
});
export type ConfirmarPagoVerificadoDTO = z.infer<typeof ConfirmarPagoVerificadoSchema>;

/** Sprint 7.2: marcar/desmarcar "el cliente pidió la cuenta". */
export const MarcarCuentaSolicitadaSchema = z.object({
  pedidoId: z.number().int().positive(),
  solicitada: z.boolean(),
});
export type MarcarCuentaSolicitadaDTO = z.infer<typeof MarcarCuentaSolicitadaSchema>;

/**
 * Sprint 6 RN-S6-03: dividir un pedido en N sub-pedidos por items.
 * - Mínimo 2 grupos (dividir en 1 no tiene sentido).
 * - Cada item sólo puede aparecer en UN grupo (validación antifraude).
 */
export const DividirCuentaSchema = z
  .object({
    pedidoId: z.number().int().positive(),
    grupos: z.array(GrupoCuentaSchema).min(2, 'Debe haber al menos 2 grupos para dividir'),
    usuarioId: z.number().int().positive(),
  })
  .refine(
    (data) => {
      const todos = data.grupos.flatMap((g) => g.items);
      return new Set(todos).size === todos.length;
    },
    { message: 'Un item no puede aparecer en más de un grupo' },
  );
export type DividirCuentaDTO = z.infer<typeof DividirCuentaSchema>;

// =============================================================================
// SPRINT 8 — DESPACHO A REPARTIDOR
// =============================================================================

/** Asignar repartidor a un pedido `listo` (delivery). Transición listo → en_ruta. */
export const DespachoAsignarSchema = z.object({
  pedidoId: z.number().int().positive(),
  /**
   * Sprint 76 — Opcional: el nombre suele venir capturado desde la cotización, y entonces
   * despachar es confirmarlo de un clic. Si no viene ninguno —ni aquí ni en el pedido—, el
   * servicio rechaza: un paquete no sale del negocio sin que quede escrito con quién se fue.
   */
  nombreRepartidor: z
    .string()
    .trim()
    .min(2, 'El nombre del repartidor debe tener al menos 2 caracteres')
    .max(80)
    .optional(),
});
export type DespachoAsignarDTO = z.infer<typeof DespachoAsignarSchema>;

/** Marcar pedido `en_ruta` como entregado. */
export const DespachoEntregadoSchema = z.object({
  pedidoId: z.number().int().positive(),
});
export type DespachoEntregadoDTO = z.infer<typeof DespachoEntregadoSchema>;

/**
 * Marcar pedido `en_ruta` como NO entregado.
 * Antifraude: requiere PIN dueña + motivo ≥5 chars (RN-S8-04, RN-S8-08).
 */
export const DespachoNoEntregadoSchema = z.object({
  pedidoId: z.number().int().positive(),
  motivo: z
    .string()
    .trim()
    .min(5, 'El motivo debe tener al menos 5 caracteres')
    .max(500),
  pinDueno: z.string().regex(/^\d{4}$/, 'PIN del supervisor inválido'),
});
export type DespachoNoEntregadoDTO = z.infer<typeof DespachoNoEntregadoSchema>;

// =============================================================================
// SPRINT 76 — A DOMICILIO: COMPLETAR EL ENVÍO Y ESPERAR EL PAGO
// =============================================================================

/**
 * Completar el costo del envío de un pedido que se dejó esperando la cotización.
 *
 * `costoEnvio` en **centavos**, como todo el dinero del sistema desde el Sprint 39. El tope de
 * $100,000 no es burocracia: es lo que impide que un dedazo (un cero de más al teclear con
 * prisa) mueva el total de un pedido a domicilio a una cifra absurda que después hay que
 * perseguir en el corte.
 */
export const ActualizarEnvioSchema = z.object({
  pedidoId: z.number().int().positive(),
  costoEnvio: z
    .number()
    .int('El costo del envío se maneja en centavos')
    .min(0, 'El costo del envío no puede ser negativo')
    .max(10_000_000, 'Ese costo de envío parece un error de dedo'),
  envioPagaCliente: z.boolean(),
  /**
   * Sprint 77 — El envío corre por cuenta del negocio.
   *
   * Es el tercer caso, el que faltaba: ni lo cobra el repartidor ni lo paga el cliente con la
   * comida. Pasa de verdad (cliente frecuente, disculpa por un retraso) y hasta ahora no se podía
   * ni registrar, así que nadie sabía cuánto costaba al mes.
   */
  envioCortesia: z.boolean().optional(),
  nombreRepartidor: z.string().trim().min(2).max(80).optional(),
  /**
   * Sprint 137 — El pedido se cocina sin haber cobrado y se cobra al entregarlo.
   *
   * Va acompañado obligatoriamente de `usuarioId`, que **no lo manda la pantalla**: lo inyecta
   * `firmadoPorLaSesion` del lado del servidor. Autorizar comida a crédito con un identificador
   * que viene del navegador sería dejar que cualquiera firme con el nombre de otro.
   */
  pagaAlEntregar: z.boolean().optional(),
  /**
   * Con cuánto va a pagar, en centavos. Sólo para que la moto salga con el cambio.
   *
   * El tope es el mismo criterio que el costo del envío: un número enorme aquí no es un pago
   * grande, es un dedo que resbaló en el teclado numérico.
   */
  pagaCon: z
    .number()
    .int('El monto se maneja en centavos')
    .positive('Con cuánto paga tiene que ser mayor que cero')
    .max(10_000_000, 'Ese monto parece un error de dedo')
    .optional(),
  /** Lo pone el servidor desde la sesión; si llega del cliente, se ignora y se sobreescribe. */
  usuarioId: z.number().int().positive().optional(),
  /** DT-080 — versión que la pantalla tenía al leer el pedido. */
  version: z.number().int().nonnegative().optional(),
});
export type ActualizarEnvioDTO = z.infer<typeof ActualizarEnvioSchema>;

/**
 * Sprint 77 — Qué se ha cobrado por llevar a ese rumbo.
 *
 * Sin coordenadas no hay nada que aprender del histórico (la agrupación es por cercanía, no por
 * el texto de la colonia), pero las zonas configuradas se devuelven igual: son la referencia que
 * sirve desde el primer día.
 */
export const SugerenciaEnvioSchema = z.object({
  latitud: z.number().min(-90).max(90).optional(),
  longitud: z.number().min(-180).max(180).optional(),
});
export type SugerenciaEnvioDTO = z.infer<typeof SugerenciaEnvioSchema>;

/** Una zona de reparto con su tarifa: los atajos que aparecen al cotizar. */
export const ZonaEnvioSchema = z.object({
  nombre: z
    .string()
    .trim()
    .min(2, 'Ponle el nombre con el que la conoces')
    .max(60),
  /** Centavos. El tope evita que un cero de más quede guardado como tarifa "normal". */
  tarifa: z
    .number()
    .int('La tarifa se maneja en centavos')
    .min(0)
    .max(10_000_000, 'Esa tarifa parece un error de dedo'),
  activa: z.boolean().optional(),
});
export type ZonaEnvioDTO = z.infer<typeof ZonaEnvioSchema>;

/**
 * DT-072 — Registrar la devolución del dinero de un pedido que no se entregó.
 *
 * La justificación es obligatoria porque este movimiento saca dinero de la caja y alguien va a
 * tener que explicarlo dentro de tres semanas.
 */
export const RegistrarDevolucionSchema = z.object({
  pedidoId: z.number().int().positive(),
  justificacion: z
    .string()
    .trim()
    .min(5, 'Escribe cómo se le devolvió el dinero al cliente')
    .max(500),
  /**
   * Quién registró la devolución. El valor lo pone `firmadoPorLaSesion`, no el payload: un
   * movimiento de caja firmado por quien lo mande es peor que uno sin firma, porque se ve como
   * evidencia (hallazgo de la auditoría del Sprint 64).
   */
  usuarioId: z.number().int().positive(),
});
export type RegistrarDevolucionDTO = z.infer<typeof RegistrarDevolucionSchema>;

/**
 * "Espéralo otro rato".
 *
 * Entre 1 minuto y 8 horas: más allá de eso no es una prórroga, es olvidarse del pedido — y un
 * pedido olvidado con la comida sin cocinar es exactamente lo que el tablero existe para evitar.
 */
export const ProrrogarPagoSchema = z.object({
  pedidoId: z.number().int().positive(),
  minutos: z
    .number()
    .int()
    .min(1, 'La prórroga mínima es de 1 minuto')
    .max(480, 'Para más de 8 horas, mejor cancela el pedido'),
});
export type ProrrogarPagoDTO = z.infer<typeof ProrrogarPagoSchema>;

// =============================================================================
// MESAS
// =============================================================================

export const MesaSchema = z.object({
  numero: z.number().int().positive(),
  capacidad: z.number().int().positive(),
  observaciones: z.string().max(200).optional(),
});
export type MesaDTO = z.infer<typeof MesaSchema>;

// =============================================================================
// SPRINT 7 — INSUMOS, PAQUETES CON SLOTS, CIERRE DEL DÍA
// =============================================================================

const UnidadInsumoEnum = z.enum(['kg', 'pieza', 'litro']);

export const CrearInsumoSchema = z.object({
  nombre: z.string().min(1).max(100),
    /** Sprint 165 — La persona confirmó que es otro producto aunque el nombre se parezca a uno que ya existe. */
    nombreParecidoConfirmado: z.boolean().optional(),
  unidad: UnidadInsumoEnum,
  stockMinimo: z.number().min(0).default(0),
  /** Sprint 13 — % que se pierde al cocinar. 0-99. */
  mermaCoccionPct: z.number().min(0).max(99).default(0),
  /**
   * Sprint 61 — Mínima cantidad utilizable. `null` = cualquier cantidad. Se acota
   * por arriba a 1: una fracción mayor no significa nada ("se usa en pasos de 5
   * piezas" no es divisibilidad, es un empaque).
   */
  fraccionMinima: z.number().positive().max(1).nullable().optional(),
  /** Sprint 12.1e — opcional. Si > 0 registra CARGA_INICIAL automática. */
  cantidadInicial: z.number().min(0).optional(),
  /** Sprint 12.1e — requerido si cantidadInicial > 0 (auditoría del movimiento). */
  usuarioId: z.number().int().positive().optional(),
});
export type CrearInsumoDTO = z.infer<typeof CrearInsumoSchema>;

export const ActualizarInsumoSchema = z.object({
  nombre: z.string().min(1).max(100).optional(),
  unidad: UnidadInsumoEnum.optional(),
  stockMinimo: z.number().min(0).optional(),
  /** Sprint 13 — editable: si cambia proveedor, ajusta el %. */
  mermaCoccionPct: z.number().min(0).max(99).optional(),
  fraccionMinima: z.number().positive().max(1).nullable().optional(),
  activo: z.boolean().optional(),
});
export type ActualizarInsumoDTO = z.infer<typeof ActualizarInsumoSchema>;

export const CargaDiariaSchema = z.object({
  cargas: z
    .array(
      z.object({
        insumoId: z.number().int().positive(),
        cargado: z.number().min(0),
        mermaPct: z.number().min(0).max(100),
      }),
    )
    .min(1, 'Debe cargar al menos un insumo'),
  usuarioId: z.number().int().positive(),
  // Sprint 165 — Declarado: Zod descarta en silencio lo que no conoce (lección del Sprint 137).
  claveIntento: claveIntentoSchema,
  /** Sprint 165 — La persona confirmó que la misma carga de hace un momento es otra (llegaron dos cajas). */
  repetidaConfirmada: z.boolean().optional(),
});
export type CargaDiariaDTO = z.infer<typeof CargaDiariaSchema>;

export const RegistrarMermaSchema = z.object({
  insumoId: z.number().int().positive(),
  cantidad: z.number().positive('La cantidad debe ser mayor a 0'),
  motivo: z.string().min(3, 'El motivo debe tener al menos 3 caracteres').max(500),
  usuarioId: z.number().int().positive(),
});
export type RegistrarMermaDTO = z.infer<typeof RegistrarMermaSchema>;

/**
 * Sprint 79 — "Llegó más" desde la venta. El tope evita que un dedazo cargue mil kilos de carne.
 */
/**
 * Sprint 81 — Unir un producto con su ingrediente.
 *
 * `cantidadPorVenta` es cuánto se descuenta al vender una unidad: para una bebida, 1 pieza; para
 * "Lomo 1/4 kg", 0.25 kg. Lo confirma una persona, nunca se deduce solo.
 */
export const VincularInsumoSchema = z.object({
  productoId: z.number().int().positive(),
  insumoId: z.number().int().positive(),
  cantidadPorVenta: z
    .number()
    .positive('¿Cuánto se descuenta al vender uno?')
    .max(10_000),
});
export type VincularInsumoDTO = z.infer<typeof VincularInsumoSchema>;

export const ReponerPorProductoSchema = z.object({
  productoId: z.number().int().positive(),
  cantidad: z.number().positive('¿Cuánto llegó?').max(10_000),
  usuarioId: z.number().int().positive(),
  motivo: z.string().trim().max(200).optional(),
  /**
   * Sprint 159 — Clave del intento. Va en el esquema porque Zod borra en silencio lo que no
   * conoce: sin esto, la protección contra el doble toque nunca llegaría al servicio.
   */
  claveIntento: claveIntentoSchema,
});
export type ReponerPorProductoDTO = z.infer<typeof ReponerPorProductoSchema>;

export const RegistrarAjusteSchema = z.object({
  insumoId: z.number().int().positive(),
  delta: z.number().refine((n) => n !== 0, { message: 'El delta no puede ser cero' }),
  motivo: z.string().min(3, 'El motivo debe tener al menos 3 caracteres').max(500),
  usuarioId: z.number().int().positive(),
});
export type RegistrarAjusteDTO = z.infer<typeof RegistrarAjusteSchema>;

export const EjecutarCierreSchema = z.object({
  fecha: z.string().optional(),
  usuarioId: z.number().int().positive(),
  resetStock: z.boolean().default(false),
  lecturasFisicas: z
    .array(
      z.object({
        insumoId: z.number().int().positive(),
        fisico: z.number().min(0),
      }),
    )
    .optional(),
});
export type EjecutarCierreDTO = z.infer<typeof EjecutarCierreSchema>;

// =============================================================================
// SPRINT 7.5 — PROMOCIONES
// =============================================================================

export const TipoPromocionEnum = z.enum([
  'DESCUENTO_PORCENTAJE',
  'DESCUENTO_MONTO',
  'REGALO',
  'PRECIO_FIJO',
  // Sprint 74 — "2 x $15": N unidades por un precio total. Ver `promosMotor`.
  'N_POR_PRECIO',
]);
export type TipoPromocionDTO = z.infer<typeof TipoPromocionEnum>;

/**
 * Sprint 7.5. Schema de creación / edición de una promoción.
 * Refines:
 * - vigenciaFin > vigenciaInicio
 * - DESCUENTO_PORCENTAJE / DESCUENTO_MONTO / PRECIO_FIJO requieren `valor > 0`
 * - DESCUENTO_PORCENTAJE: valor ∈ (0, 100]
 * - REGALO requiere `productoRegaloId` y `cantidadRegalo > 0`, no usa `valor`
 * - Debe haber al menos 1 producto o 1 paquete elegible
 */
export const PromocionSchema = z
  .object({
    nombre: z.string().min(1).max(80),
    descripcion: z.string().max(500).optional(),
    tipo: TipoPromocionEnum,
    valor: z.number().positive().optional(),
    topeMonto: z.number().positive().optional(),
    productoRegaloId: z.number().int().positive().optional(),
    cantidadRegalo: z.number().int().positive().optional(),
    cantidadMinima: z.number().int().min(1).default(1),
    /** Sprint 22 — subtotal mínimo del pedido para gatillar la promo. */
    montoMinimoPedido: z.number().positive().optional(),
    /** Sprint 22 — sólo REGALO: true = sustractivo (cupón), false = aditivo (cortesía). */
    regaloDescuentaDelTotal: z.boolean().default(false),
    vigenciaInicio: z.string().min(1, 'vigenciaInicio requerida'),
    vigenciaFin: z.string().min(1, 'vigenciaFin requerida'),
    diasSemana: DiasSemanaSchema.optional(),
    /**
     * Sprint 74 — Ventana de horas en minutos desde la medianoche.
     *
     * `nullable` además de `optional` porque el formulario manda `null` explícito al destildar la
     * casilla: si sólo fuera opcional, quitar el horario dejaría el anterior guardado.
     */
    horaInicio: z.number().int().min(0).max(1439).nullable().optional(),
    horaFin: z.number().int().min(0).max(1439).nullable().optional(),
    /** Sprint 74 — Sólo N_POR_PRECIO: el "2" de "2 x $15". Un grupo de 1 no es una promoción. */
    cantidadPaquete: z.number().int().min(2).optional(),
    acumulable: z.boolean().default(false),
    activa: z.boolean().default(true),
    prioridad: z.number().int().min(0).default(100),
    productosElegiblesIds: z.array(z.number().int().positive()).default([]),
    paquetesElegiblesIds: z.array(z.number().int().positive()).default([]),
  })
  .refine(
    (p) => new Date(p.vigenciaFin).getTime() > new Date(p.vigenciaInicio).getTime(),
    { message: 'vigenciaFin debe ser posterior a vigenciaInicio', path: ['vigenciaFin'] },
  )
  .refine(
    (p) => {
      if (p.tipo === 'DESCUENTO_PORCENTAJE') return p.valor !== undefined && p.valor > 0 && p.valor <= 100;
      if (p.tipo === 'DESCUENTO_MONTO' || p.tipo === 'PRECIO_FIJO') return p.valor !== undefined && p.valor > 0;
      // Sprint 74 — N_POR_PRECIO necesita el precio del grupo. Sin esto pasaría con `valor` vacío
      // y el motor cobraría $0 por cada grupo: comida regalada por un refine que no se actualizó.
      if (p.tipo === 'N_POR_PRECIO') return p.valor !== undefined && p.valor > 0;
      return true;
    },
    { message: 'valor inválido para el tipo de promoción', path: ['valor'] },
  )
  .refine(
    // Sprint 74 — Y necesita saber de cuántas unidades es el grupo.
    (p) => p.tipo !== 'N_POR_PRECIO' || (p.cantidadPaquete !== undefined && p.cantidadPaquete >= 2),
    {
      message: 'Falta cuántas unidades entran en el precio (el "2" de "2 x $15")',
      path: ['cantidadPaquete'],
    },
  )
  .refine(
    /*
      Sprint 74 — Las dos horas van juntas o ninguna.

      Con una sola, `dentroDeVentana` la ignora y la promoción aplicaría todo el día: la dueña
      creería haber puesto un horario que el motor no respeta. Es peor que rechazarlo.
    */
    (p) => (p.horaInicio ?? null) === null === ((p.horaFin ?? null) === null),
    {
      message: 'Pon las dos horas o ninguna: con una sola la promoción aplicaría todo el día',
      path: ['horaFin'],
    },
  )
  .refine(
    (p) => {
      if (p.tipo !== 'REGALO') return true;
      return (
        p.productoRegaloId !== undefined &&
        p.cantidadRegalo !== undefined &&
        p.cantidadRegalo > 0
      );
    },
    { message: 'REGALO requiere productoRegaloId y cantidadRegalo > 0', path: ['productoRegaloId'] },
  )
  .refine(
    (p) => p.tipo !== 'DESCUENTO_PORCENTAJE' || p.topeMonto === undefined || p.topeMonto > 0,
    { message: 'topeMonto debe ser > 0 cuando se especifica', path: ['topeMonto'] },
  )
  .refine(
    (p) => p.productosElegiblesIds.length > 0 || p.paquetesElegiblesIds.length > 0,
    {
      message: 'Debe haber al menos 1 producto o paquete elegible',
      path: ['productosElegiblesIds'],
    },
  )
  .refine(
    (p) => {
      const set = new Set(p.productosElegiblesIds);
      return set.size === p.productosElegiblesIds.length;
    },
    { message: 'productosElegiblesIds duplicados', path: ['productosElegiblesIds'] },
  )
  .refine(
    (p) => {
      const set = new Set(p.paquetesElegiblesIds);
      return set.size === p.paquetesElegiblesIds.length;
    },
    { message: 'paquetesElegiblesIds duplicados', path: ['paquetesElegiblesIds'] },
  );
export type PromocionDTO = z.infer<typeof PromocionSchema>;

/** Item del carrito que entra al motor (pure-function). */
export const MotorCarritoItemSchema = z
  .object({
    productoId: z.number().int().positive().optional(),
    paqueteId: z.number().int().positive().optional(),
    cantidad: z.number().int().positive(),
    precioUnitario: z.number().min(0),
    tienePrecioOverride: z.boolean().optional().default(false),
  })
  .refine((i) => Boolean(i.productoId) !== Boolean(i.paqueteId), {
    message: 'Item debe tener productoId XOR paqueteId',
  });
export type MotorCarritoItemDTO = z.infer<typeof MotorCarritoItemSchema>;

// =============================================================================
// SPRINT 12.1b — PROPINAS Y MOVIMIENTOS DE CAJA
// =============================================================================

export const ReasignarPropinaSchema = z.object({
  pagoId: z.number().int().positive(),
  nuevoUsuarioId: z.number().int().positive(),
});

export const SimularRepartoSchema = z.object({
  fechaInicio: z.string().min(1, 'Fecha inicio requerida'),
  fechaFin: z.string().min(1, 'Fecha fin requerida'),
  criterio: z.enum(['mesero', 'igualitario']),
  usuariosIgualitario: z.array(z.number().int().positive()).optional(),
}).refine(
  (d) => d.criterio !== 'igualitario' || (d.usuariosIgualitario && d.usuariosIgualitario.length > 0),
  { message: 'Reparto igualitario requiere al menos 1 usuario seleccionado', path: ['usuariosIgualitario'] },
);

export const EjecutarRepartoSchema = SimularRepartoSchema.and(
  z.object({ usuarioEjecutorId: z.number().int().positive() }),
);

export const CancelarRepartoSchema = z.object({
  repartoId: z.number().int().positive(),
  motivo: z.string().min(5, 'El motivo debe tener al menos 5 caracteres').max(500),
  usuarioId: z.number().int().positive(),
});

export const MarcarEntregadaSchema = z.object({
  detalleId: z.number().int().positive(),
  entregada: z.boolean(),
});

export const CATEGORIAS_EGRESO_VALIDAS = [
  'GASTO_PROVEEDOR',
  'GASTO_OPERATIVO',
  'ADELANTO_EMPLEADO',
  'OTRO',
] as const;
/**
 * Sprint 77 (DT-072) — Categoría de sistema para devolver el dinero de un pedido no entregado.
 *
 * **Fuera de `CATEGORIAS_EGRESO_VALIDAS` a propósito**: no se registra a mano desde la pantalla de
 * movimientos, igual que el reparto de propinas. La escribe el sistema al resolver la devolución,
 * enlazada al pedido — si se pudiera teclear suelta, el movimiento existiría sin que el pedido
 * dejara de aparecer como pendiente, y el negocio acabaría devolviendo dos veces.
 */
export const CATEGORIA_DEVOLUCION_CLIENTE = 'DEVOLUCION_CLIENTE';
export const CATEGORIAS_INGRESO_VALIDAS = [
  'INGRESO_EXTRA',
  'APORTACION_DUENO',
  'OTRO',
] as const;

export const RegistrarMovimientoCajaSchema = z.object({
  tipo: z.enum(['INGRESO', 'EGRESO']),
  categoria: z.string().min(1),
  monto: z.number().positive('El monto debe ser mayor a 0'),
  justificacion: z.string().min(5, 'La justificación debe tener al menos 5 caracteres').max(500),
  usuarioId: z.number().int().positive(),
}).refine(
  (d) => {
    const validas = d.tipo === 'EGRESO' ? CATEGORIAS_EGRESO_VALIDAS : CATEGORIAS_INGRESO_VALIDAS;
    return (validas as readonly string[]).includes(d.categoria);
  },
  { message: 'Categoría no válida para el tipo seleccionado', path: ['categoria'] },
);

// =============================================================================
// TICKETS — Sprint 56 (impresión desde navegador)
// =============================================================================

/**
 * Parámetros de `ticket:html`. El renderer es hostil por definición: el tipo se
 * restringe a la enumeración y `pedidoId` es obligatorio salvo en la prueba de
 * impresión, que no toca ningún pedido.
 */
/**
 * Sprint 60 — Retiro de un producto del menú.
 *
 * `ajustarPrecioDe` va como lista de paquetes en vez de un booleano global: el
 * operador decide paquete por paquete si absorbe la diferencia o la traslada.
 */
export const RetirarProductoSchema = z.object({
  productoId: z.number().int().positive(),
  decisiones: z
    .array(
      z.object({
        renglonId: z.number().int().positive(),
        accion: z.enum(['sustituir', 'quitar', 'opcional']),
        sustitutoId: z.number().int().positive().optional(),
      }),
    )
    .max(100),
  ajustarPrecioDe: z.array(z.number().int().positive()).max(100).optional(),
}).refine(
  (d) => d.decisiones.every((x) => x.accion !== 'sustituir' || typeof x.sustitutoId === 'number'),
  { message: 'Falta elegir el producto que reemplaza', path: ['decisiones'] },
);

/** Sprint 58 — Parámetros de `impresion:encolar`. */
export const EncolarImpresionSchema = z.object({
  tipo: z.enum(['caja', 'cocina', 'despacho', 'prueba']),
  pedidoId: z.number().int().positive().optional(),
  itemIds: z.array(z.number().int().positive()).max(200).optional(),
}).refine(
  (d) => d.tipo === 'prueba' || typeof d.pedidoId === 'number',
  { message: 'pedidoId es obligatorio para tickets de caja y cocina', path: ['pedidoId'] },
);

export const TicketHtmlParamsSchema = z.object({
  tipo: z.enum(['caja', 'cocina', 'despacho', 'prueba']),
  pedidoId: z.number().int().positive().optional(),
  /**
   * Sprint 56.2 — Comanda de sólo lo agregado a una mesa. El acotado real lo da
   * la relación con `pedidoId`: ids de otro pedido no traen nada.
   */
  itemIds: z.array(z.number().int().positive()).max(200).optional(),
}).refine(
  (d) => d.tipo === 'prueba' || typeof d.pedidoId === 'number',
  { message: 'pedidoId es obligatorio para tickets de caja y cocina', path: ['pedidoId'] },
);

/**
 * Sprint 136 — Presencia y chat.
 *
 * `usuarioId` viaja pero el servidor lo **pisa** con el de la sesión (`firmadoPorLaSesion`): de
 * quién es un mensaje no lo decide el navegador. En un chat que sirve para pedir ayuda con dinero
 * de por medio, suplantar a alguien no es una travesura.
 */
export const MarcarPresenciaSchema = z.object({
  usuarioId: z.number().int().positive(),
  pantalla: z.string().trim().max(60).optional(),
});

export const EnviarMensajeSchema = z.object({
  usuarioId: z.number().int().positive(),
  paraUsuarioId: z.number().int().positive().nullable().optional(),
  texto: z.string().trim().min(1, 'Escribe algo antes de enviar').max(2000),
  pantalla: z.string().trim().max(60).optional(),
  /** "Enséñame lo que ves": el estado de la pantalla en texto, no una imagen. */
  contexto: z.string().max(4000).optional(),
});

/**
 * Sprint 136 — La llave del ayudante.
 *
 * `null` borra la que hubiera. El formato se comprueba de verdad al guardar —con una llamada
 * mínima al servicio— porque una llave con la forma correcta y sin crédito falla igual.
 */
export const GuardarApiKeySchema = z.object({
  apiKey: z.string().trim().min(20).max(200).nullable(),
});
